/**
 * Builds payload for POST /servicerequest/refer.
 * Aligned with InBoundCall encounter and waiting-room shapes.
 */

import moment from "moment";

export const buildFhirReference = (type, id, display = "") => ({
    id,
    reference: `${type}/${id}`,
    display,
});

const findTaggedQuestion = (questionResponse, tag) =>
    questionResponse?.find((item) => item.tag === tag);

const getTagTextAnswer = (questionResponse, tag) =>
    findTaggedQuestion(questionResponse, tag)?.answer?.[0]?.answer;

const getTagRadioOption = (questionResponse, tag) =>
    findTaggedQuestion(questionResponse, tag)?.answer?.find((val) => val?.answer === true)
        ?.option;

const getTagDropDownAnswer = (questionResponse, tag) =>
    findTaggedQuestion(questionResponse, tag)?.answer?.find((val) => val?.answer !== "")
        ?.answer;

/**
 * Consolidated intakeQuestions for WR — mirrors editEncounter intakeQuestions
 * (InBoundCall + AddToWaitingRoomModal).
 */
export const buildWaitingRoomIntakeQuestions = ({
    questionResponse,
    reasonForReferral,
    patientDisplay,
    waitingRoom,
    questionnaireResponseId,
}) => {
    const intakeQuestions = {
        preferredChannel: "phone",
    };

    if (questionnaireResponseId) {
        intakeQuestions.questionnaireResponseId = {
            reference: `QuestionnaireResponse/${questionnaireResponseId}`,
        };
    }

    const callerQuestion = findTaggedQuestion(questionResponse, "CALLER");
    let callerName = patientDisplay || "";
    if (callerQuestion?.answer?.[0]?.answer) {
        const first = callerQuestion.answer[0]?.answer || "";
        const last = callerQuestion.answer[1]?.answer || "";
        callerName = last ? `${first} ${last}`.trim() : first;
    }
    if (callerName) {
        intakeQuestions.callerName = callerName;
    }

    const telephone = getTagTextAnswer(questionResponse, "TELEPHONE");
    if (telephone) {
        const parts = String(telephone).split(/\s+ext\s+/i);
        intakeQuestions.callerNumber = parts[0]?.trim();
        if (parts[1]?.trim()) {
            intakeQuestions.callerNumberExt = parts[1].trim();
        }
    }

    if (reasonForReferral?.trim()) {
        intakeQuestions.intakeReason = reasonForReferral.trim();
    } else {
        const reasonFromTag = getTagTextAnswer(questionResponse, "REASON");
        if (reasonFromTag) {
            intakeQuestions.intakeReason = reasonFromTag;
        }
    }

    const selfCalling = getTagRadioOption(questionResponse, "SELFCALLING");
    if (selfCalling === "Yes") {
        intakeQuestions.callerRelationship = "Self";
    } else {
        const relationship = getTagDropDownAnswer(questionResponse, "RELATIONSHIP");
        if (relationship) {
            intakeQuestions.callerRelationship = relationship;
        }
    }

    const location = getTagDropDownAnswer(questionResponse, "LOCATION");
    if (location) {
        intakeQuestions.location = location;
    }

    if (waitingRoom?.communicationChannel) {
        intakeQuestions.intakeCommunicationChannel = waitingRoom.communicationChannel;
    }
    if (waitingRoom?.category) {
        intakeQuestions.intakePriorityReason = waitingRoom.category;
    }
    if (waitingRoom?.priorityLevel != null) {
        intakeQuestions.intakePriorityLevel = waitingRoom.priorityLevel;
    }
    if (waitingRoom?.assignedPractitioner) {
        intakeQuestions.intakeAssignedPractitioner = {
            individual: {
                display: waitingRoom.assignedPractitioner.display,
                reference: waitingRoom.assignedPractitioner.reference,
                type: "Practitioner",
            },
            period: {
                start: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
                end: null,
            },
        };
    }

    return intakeQuestions;
};

const mapQuestionResponse = (questionResponse) =>
    questionResponse?.map((item) => ({
        id: item?.linkId,
        tag: item?.tag,
        questionId: item?.id || item?.questionId || null,
        question: item?.text,
        answer: item?.answer,
        questionType: item?.answerType || item?.questionType,
        required: item?.required,
    }));

export const buildQuestionnairePayload = ({ questionnaireId, questionResponse, author }) => {
    if (!questionnaireId || !questionResponse?.length || !author?.authorId) {
        return null;
    }

    return {
        questionnaireId,
        questionResponse: mapQuestionResponse(questionResponse),
        author: {
            authorId: author.authorId,
            authorType: author.authorType || "Practitioner",
            authorName: author.authorName || "",
        },
    };
};

export const buildWaitingRoomPayload = (
    {
        waitingRoomCategory,
        priorityLevel,
        assignedPractitioner,
        communicationChannel,
    },
    intakeContext
) => {
    const waitingRoom = {
        category: waitingRoomCategory,
        priorityLevel,
        communicationChannel,
        assignedPractitioner: assignedPractitioner
            ? buildFhirReference(
                "Practitioner",
                assignedPractitioner.id,
                assignedPractitioner.name?.[0]?.text
            )
            : null,
    };

    if (intakeContext) {
        waitingRoom.intakeQuestions = buildWaitingRoomIntakeQuestions({
            ...intakeContext,
            waitingRoom,
        });
    }

    return waitingRoom;
};

export const mapToReferralType = (proceedWith) => {
    if (proceedWith === "WAITING_ROOM") return "waitingRoom";
    if (proceedWith === "SCHEDULE") return "schedule";
    return proceedWith;
};

export const buildInternalReferralPayload = ({
    sourceEncounterId,
    patient,
    organization,
    serviceLine,
    proceedWith,
    referralType,
    reasonForReferral,
    patientLocation,
    questionnaire,
    waitingRoom,
    appointment,
}) => {
    const payload = {
        resourceType: "serviceRequest",
        internalReferral: true,
        proceedWith,
        referralType,
        sourceEncounterId,
        patient,
        organization,
        serviceLine,
        reasonForReferral,
    };

    if (appointment) {
        payload.appointment = appointment;
    }

    if (patientLocation) {
        payload.patientLocation = patientLocation;
    }

    if (questionnaire) {
        payload.questionnaire = questionnaire;
    }

    if (referralType === "waitingRoom" && waitingRoom) {
        payload.waitingRoom = waitingRoom;
    }

    return payload;
};
